import { protectedResources, graphConfig } from '../../authConfig';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { SetupInstance } from '../../app/msal-account-helpers';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { CacheService } from '../cache-service';
import { msalConfig } from '../../app';

class GraphAPI {

    constructor(instance, inProgress, accounts, forceRefresh) {
        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;

        // Do not pin to accounts[0]; rely on MSAL active account
        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.user.scopes.read
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            // account is selected by AccessTokenHelper via instance.getActiveAccount()
        };

        this.accessTokenService = new AccessTokenHelper();
    }

    // Central helper for Graph tokens via MSAL
    getToken = async () => {
        return this.accessTokenService.getAccessToken(
            this.instance,
            this.inProgress,
            this.accessTokenRequest
        );
    };

    // Used by MenuProvider to drive menu role logic
    getSPAAccessToken = async () => {
        try {
            const response = await this.getToken();
            return response;
        } catch (error) {
            console.error('Failed to get SPA access token:', error);
            throw error;
        }
    };

    getApplicationRoles = () => {
        return polly()
            .logger(error => {
                console.error(error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                if (this.inProgress !== InteractionStatus.None) {
                    throw new Error('Login interaction in-progress');
                }

                try {
                    const token = await this.getToken();
                    const roles = token?.idTokenClaims?.roles || [];
                    return Object.assign([], roles);
                } catch (error) {
                    if (error instanceof InteractionRequiredAuthError) {
                        // Centralized auth (AuthGate/AccessTokenHelper) will handle redirects
                    }
                    console.log(error);
                    throw error;
                }
            });
    };

    getMeDetails = () => {
        return polly()
            .logger(error => {
                console.error(error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                if (this.inProgress !== InteractionStatus.None) {
                    throw new Error('Login interaction in-progress');
                }

                try {
                    const token = await this.getToken();

                    // Build a per-account cache key for fallback cache (30 min TTL)
                    const accountKey = (token && token.account && token.account.homeAccountId) ||
                        (token && token.idTokenClaims && (token.idTokenClaims.oid || token.idTokenClaims.sub)) ||
                        'default';
                    const cacheKey = `graph_me_profile_${accountKey}`;
                    const headers = new Headers();
                    const bearer = `Bearer ${token.accessToken}`;

                    headers.append('Authorization', bearer);

                    const options = {
                        method: 'GET',
                        headers: headers,
                    };

                    const response = await fetch(graphConfig.graphMeEndpoint, options);
                    const data = await response.json();

                    // Ensure we always expose the Entra Object Id (GUID)
                    const objectId = (token && token.idTokenClaims && (token.idTokenClaims.oid || token.idTokenClaims.sub)) || data.id;
                    const result = Object.assign({}, data, { objectId });

                    // Cache profile for this account for up to 30 minutes
                    CacheService.set(cacheKey, result);

                    return result;
                } catch (error) {
                    // On auth interaction errors, do not hide the problem with cache
                    if (error instanceof InteractionRequiredAuthError) {
                        console.log(error);
                        throw error;
                    }

                    console.log(error);

                    // For non-auth errors, try to fall back to a cached profile if available
                    try {
                        const token = await this.getToken();
                        const accountKey = (token && token.account && token.account.homeAccountId) ||
                            (token && token.idTokenClaims && (token.idTokenClaims.oid || token.idTokenClaims.sub)) ||
                            'default';
                        const cacheKey = `graph_me_profile_${accountKey}`;
                        // In fallback mode, ignore TTL and use any cached value if present
                        const effectivelyInfiniteTtl = Number.MAX_SAFE_INTEGER;
                        const cached = CacheService.get(cacheKey, effectivelyInfiniteTtl);
                        if (cached) {
                            return cached;
                        }
                    } catch (fallbackError) {
                        console.log('Failed to load fallback cached Graph profile', fallbackError);
                    }

                    throw error;
                }
            });
    };

    // Fetch a specific user from Graph by id
    getUser = (userId) => {
        return polly()
            .logger(error => {
                console.error(error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                if (this.inProgress !== InteractionStatus.None) {
                    throw new Error('Login interaction in-progress');
                }

                try {
                    const token = await this.getToken();

                    const headers = new Headers();
                    const bearer = `Bearer ${token.accessToken}`;

                    headers.append('Authorization', bearer);

                    const options = {
                        method: 'GET',
                        headers: headers,
                    };

                    const fetchUrl = graphConfig.graphUsersEndpoint(userId);
                    const response = await fetch(fetchUrl, options);
                    const data = await response.json();
                    return Object.assign({}, data);
                } catch (error) {
                    if (error instanceof InteractionRequiredAuthError) {
                        // Centralized auth (AuthGate/AccessTokenHelper) will handle redirects
                    }
                    console.log(error);
                    throw error;
                }
            });
    };

    fetchProfilePhoto = () => {
        return polly()
            .handle((err) => {
                // Check if the error is a 404
                if (err.response && err.response.status === 404) {
                    return false; // Do not retry on 404 errors
                }
                return true; // Retry for other errors
            })
            .logger(error => {
                console.error(error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(async () => {
                if (this.inProgress !== InteractionStatus.None) {
                    throw new Error('Login interaction in-progress');
                }

                try {
                    const token = await this.getToken();

                    const headers = new Headers();
                    const bearer = `Bearer ${token.accessToken}`;

                    headers.append('Authorization', bearer);

                    const options = {
                        method: 'GET',
                        headers: headers,
                    };

                    return fetch(graphConfig.graphMePhotoEndpoint, options);
                } catch (error) {
                    if (error instanceof InteractionRequiredAuthError) {
                        // Centralized auth (AuthGate/AccessTokenHelper) will handle redirects
                    }
                    console.log(error);
                    throw error;
                }
            })
            .then((response) => {
                if (response.ok) {
                    return response.blob();
                }
                throw new Error('Image was not loaded');
            })
            .then((blob) => {
                return URL.createObjectURL(blob);
            });
    };
}

export default GraphAPI;